<template>
    <modal :show="true" :closable="!loading" @close="closeConfirm" :custom_modal_class="'modal-lg fullwidth-modal'">
        <div slot="header">
            <div class="modal-header">
                <h5 class="modal-title">{{ $t('files.edit_photo') }}</h5>
                <button type="button" class="close" @click="closeConfirm" aria-label="Close" v-if="!loading">
                    <span aria-hidden="true">×</span>
                </button>
            </div>
        </div>

        <div slot="body">
            <div class="form-group ">
                <label class="form-control-label">{{ $t('files.name') }}</label>
                <input type="text" class="form-control" v-model="requestData.name" :disabled="loading">
            </div>

            <div class="cropper-wrapper" :class="{ 'disabled' : loading }">
                <div class="cropper-loading" v-if="loading">
                    <div class="spinner-border">
                        <span class="sr-only">{{ $t('general.loading') }}</span>
                    </div>
                </div>

                <div v-if="!failedToSetupEditor">
                    <div ref="editor" class="editor-canvas"></div>
                </div>

                <div v-if="failedToSetupEditor" class="alert alert-danger">
                    {{ $t('files.failed_to_setup_editor') }}
                </div>

                <FileEditorThemeLibrary
                    v-if="companySettings?.file_editor_themes_enabled && renderer"
                    :types="themeTypes"
                    :ceremony-code="ceremonyCode"
                    :file="currentFile"
                    :selected="hasThemeImage"
                    @loading="loading = $event"
                    @select="selectedTheme"
                    @remove="removeTheme"
                />
            </div>

            <div class="editor-actions" v-if="renderer">
                <div>
                    <button @click="rotate('left')" class="btn btn-sm btn-secondary" :disabled="loading" v-tooltip="$t('files.rotate_left')">
                        <icon name="undo"></icon>
                    </button>
                    <button @click="rotate('right')" class="btn btn-sm btn-secondary" :disabled="loading" v-tooltip="$t('files.rotate_right')">
                        <icon name="redo"></icon>
                    </button>

                    <button @click="scale('vertical')" class="btn btn-sm btn-secondary" :disabled="loading" v-tooltip="$t('files.flip_vertical')" style="padding: 0.5rem 1rem;">
                        <icon name="arrows-alt-v"></icon>
                    </button>
                    <button @click="scale('horizontal')" class="btn btn-sm btn-secondary" :disabled="loading" v-tooltip="$t('files.flip_horizontal')">
                        <icon name="arrows-alt-h"></icon>
                    </button>

                    <button @click="setAspectRatio" class="btn btn-sm btn-secondary" :disabled="loading" v-tooltip="$t('files.toggle_aspect_ratio')">
                        <icon v-if="!lockAspectRatio" name="compress-arrows-alt"></icon>
                        <icon v-else name="expand-arrows-alt"></icon>
                    </button>
                </div>

                <div
                    class="ml-auto btn btn-xs btn-secondary btn-color-range"
                    v-tooltip="$t('files.change_image_border_tooltip')"
                >
                    <label class="color-picker" :style="{ 'background-color': currentBorderColor }">
                        <input
                            type="color"
                            :value="currentBorderColor"
                            :disabled="loading"
                            @input="changeBorderColor($event.target.value, false)"
                            @change="changeBorderColor($event.target.value)"
                        />
                    </label>

                    <input
                        type="range"
                        :value="currentBorderSize"
                        :disabled="loading"
                        min="0"
                        max="5"
                        step="0.1"
                        @input="changeBorderSize(Number($event.target.value), false)"
                        @change="changeBorderSize(Number($event.target.value))"
                    />
                </div>

                <div
                    class="btn btn-xs btn-secondary btn-color-range"
                    v-tooltip="$t('files.change_background_color_overlay_tooltip')"
                >
                    <label class="color-picker" :style="{ 'background-color': currentBackgroundColor }">
                        <input
                            type="color"
                            :value="currentBackgroundColor"
                            :disabled="loading || !hasThemeImage"
                            @input="changeBackgroundColor($event.target.value, false)"
                            @change="changeBackgroundColor($event.target.value)"
                        />
                    </label>

                    <input
                        type="range"
                        :value="currentBackgroundColorOpacity"
                        :disabled="loading || !hasThemeImage"
                        min="0"
                        max="1"
                        step="0.01"
                        @input="changeBackgroundColorOpacity(Number($event.target.value), false)"
                        @change="changeBackgroundColorOpacity(Number($event.target.value))"
                    />
                </div>
            </div>
        </div>

        <div slot="footer" class="editor-footer">
            <button type="button" class="btn btn-secondary btn-sm mr-2" data-dismiss="modal" @click="closeConfirm" :disabled="loading">
                {{ $t('files.close') }}
            </button>

            <div class="ml-auto">
                <button type="button" class="btn btn-success btn-icon btn-sm ml-1" @click="saveAndGoto(previousFile)" :disabled="loading" v-if="previousFile">
                    {{ $t('files.save_and_previous') }}
                </button>

                <button type="button" class="btn btn-success btn-icon btn-sm ml-1" @click="saveAndGoto(nextFile)" :disabled="loading" v-if="nextFile">
                    {{ $t('files.save_and_next') }}
                </button>

                <button type="button" class="btn btn-success btn-icon btn-sm ml-1" @click="saveAndClose" :disabled="loading">
                    {{ $t('files.save') }}
                </button>
            </div>
        </div>

    </modal>
</template>

<script>

    import Modal from './Modal';
    import { Application, Assets, Container, RAD_TO_DEG, DEG_TO_RAD, Sprite, Graphics, RenderTexture, autoDetectRenderer, Texture, WRAP_MODES, TilingSprite, ColorMatrixFilter } from 'pixi.js';
    import * as Sentry from '@sentry/vue';
    import { FILE_EDITOR_THEME_TYPE } from '../enums/theme-types';
    import FileEditorThemeLibrary from './FileEditorThemeLibrary.vue';
    import { mapState } from 'vuex';
    import { debounce } from 'lodash';
    import { notifyError } from '../utils/notify';
    import { OutlineFilter } from '@pixi/filter-outline';
    import { decimalToHexString, hexToDecimal } from '../utils/color.js';

    const Type = {
        Image: 'image',
        EditorExport: 'editor_export',
        EditorBackground: 'editor_background',
        Resizer: 'resizer',
        BackgroundOverlay: 'background_overlay',
    };

    const ImageTypes = {
        Main: 'main',
        Theme: 'theme',
    };

    const FilterType = {
        Outline: 'outline',
    };

    const HANDLE_SIZE = 20;

    export default {

        components: {
            FileEditorThemeLibrary,
            Modal,
        },

        props: {
            ceremonyCode: {
                type: String,
                required: true,
            },

            file: {
                type: Object,
                required: true,
            },

            block: {
                type: Object,
                required: true,
            },
        },

        computed: {
            themeTypes() {
                return [FILE_EDITOR_THEME_TYPE.IMAGE];
            },

            fileSrc() {
                return this.currentFile.original_path.replace(/\/(:|%3A)code\//g, '/' + this.ceremonyCode + '/');
            },

            editorBackgroundContainer() {
                if (!this.renderer?.editor) {
                    return null;
                }

                return this.renderer.editor.children.find(child => child.type === Type.EditorBackground);
            },

            editorExportContainer() {
                if (!this.renderer?.editor) {
                    return null;
                }

                return this.renderer.editor.children.find(child => child.type === Type.EditorExport);
            },

            currentFileIndex() {
                return this.block.files.findIndex(file => {
                    if (this.currentFile.pivot && this.currentFile.pivot.order !== file.pivot?.order) {
                        return false;
                    }

                    return file.id === this.currentFile.id;
                });
            },

            previousFile() {
                if (this.block?.files?.length <= 1) {
                    return null;
                }

                if (this.currentFileIndex === 0) {
                    return this.block.files[this.block.files.length - 1];
                }

                return this.block.files[this.currentFileIndex - 1];
            },

            nextFile() {
                if (this.block?.files?.length <= 1) {
                    return null;
                }

                if (this.currentFileIndex + 1 >= this.block.files.length) {
                    return this.block.files[0];
                }

                return this.block.files[this.currentFileIndex + 1];
            },

            currentBorderSize() {
                return this.currentBorderFilter?.thickness ?? 0;
            },

            currentBorderFilter() {
                // Try to find the border size for the first image
                return this.editorExportContainer.children.filter(child => child.imageType === ImageTypes.Main).reduce((borderFilter, child) => {
                    if (borderFilter) {
                        return borderFilter;
                    }

                    return (child.children[0]?.filters || []).find(filter => {
                        return filter.type === FilterType.Outline;
                    });
                }, null);
            },

            currentBackgroundColor() {
                return this.currentBackgroundOverlay?.tint ?? '#000000';
            },

            currentBackgroundColorOpacity() {
                return this.currentBackgroundOverlay?.alpha ?? 0;
            },

            currentBackgroundOverlay() {
                // Try to find the border size for the first image
                return this.editorExportContainer.children.filter(child => child.imageType === ImageTypes.Theme).reduce((backgroundOverlay, child) => {
                    if (backgroundOverlay) {
                        return backgroundOverlay;
                    }

                    return child.children.find(subChild => subChild.type === Type.BackgroundOverlay);
                }, null);
            },

            ...mapState({
                companySettings: state => state.companySettings,
            }),
        },

        watch: {
            async currentFile() {
                this.requestData.name = this.currentFile.name;
                await this.reset();

                if (this.currentFile.editor_data) {
                    await this.addFromFileEditorData(this.currentFile.editor_data);
                } else {
                    await this.addImage(this.fileSrc, ImageTypes.Main);
                }

                this.updateRenderState();
            },
        },

        data() {
            return {
                failedToSetupEditor: false,

                currentFile: this.file,
                loading: false,

                requestData: {
                    name: this.file.name,
                },

                lockAspectRatio: false,

                renderer: null,
                hasAnyEditorChanges: false,
                hasThemeImage: false,

                resizing: null,
                dragging: null,

                currentBorderColor: null,
            };
        },

        mounted() {
            this.setup();
        },

        methods: {
            async setup() {
                this.failedToSetupEditor = false;

                try {
                    await this.createRenderer();

                    // Add initial images
                    if (this.currentFile.editor_data) {
                        await this.addFromFileEditorData(this.currentFile.editor_data);
                    } else {
                        await this.addImage(this.fileSrc, ImageTypes.Main);
                    }

                    this.updateRenderState();

                    window.addEventListener('resize', this.resizeWindow);
                } catch (exception) {
                    this.removeEditor();

                    this.failedToSetupEditor = true;
                    notifyError(exception);
                } finally {
                    this.loading = false;
                }
            },

            async createRenderer() {
                const app = new Application({
                    resolution: 1,
                    autoDensity: true,

                    resizeTo: this.$refs.editor,

                    backgroundAlpha: this.debug ? 0 : 1,

                    powerPreference: 'high-performance',
                });

                this.$refs.editor.appendChild(app.view);

                const container = new Container();
                const editor = await this.createEditorContainer(1920, 1080);

                this.fitEditorToScreen(editor, app.screen.width, app.screen.height);

                container.addChild(editor);

                editor.eventMode = 'static';
                editor.on('pointermove', (event) => {
                    if (this.dragging) {
                        const offsetX = (event.global.x - this.dragging.container.start.globalX) / this.renderer.editor.scale.x;
                        const offsetY = (event.global.y - this.dragging.container.start.globalY) / this.renderer.editor.scale.y;

                        this.dragging.container.target.position.x = this.dragging.container.start.x + offsetX;
                        this.dragging.container.target.position.y = this.dragging.container.start.y + offsetY;

                        this.markChange('position');
                    }
                });

                editor.on('pointerup', () => {
                    this.stopDragging();
                });

                editor.on('pointeroutside', () => {
                    this.stopDragging();
                });

                window.addEventListener('pointermove', this.resizeMouseMove);
                window.addEventListener('pointerup', this.stopDragging);

                app.stage.addChild(container);

                this.renderer = {
                    app,
                    editor,
                    container,
                };

                if (process.env.NODE_ENV === 'development') {
                    window.__PIXI_DEVTOOLS__ = {
                        app,
                    };
                }
            },

            updateRenderState() {
                this.currentBorderColor = this.currentBorderFilter?.color ? decimalToHexString(this.currentBorderFilter.color) : '#ffffff';
                this.hasThemeImage = !!(this.editorExportContainer?.children || []).find(child => child.type === Type.Image && child.imageType === ImageTypes.Theme);
            },

            resizeMouseMove(event) {
                if (this.resizing) {
                    const offsetX = (event.clientX - this.resizing.container.start.mouseX) / (this.renderer.editor.scale.x / 2);
                    const offsetY = (event.clientY - this.resizing.container.start.mouseY) / (this.renderer.editor.scale.y / 2);

                    const startScaleX = Math.abs(this.resizing.container.start.scaleX);
                    const startScaleY = Math.abs(this.resizing.container.start.scaleY);

                    const startWidth = this.resizing.container.start.width;
                    const startHeight = this.resizing.container.start.height;

                    // Convert to 0 - 1 instead of 1 - 0
                    const handleX = 1 - this.resizing.handle.start.baseX;
                    const handleY = 1 - this.resizing.handle.start.baseY;

                    let newScale = 0;

                    if (handleX === 1 && handleY === 0.5) {
                        // Middle right
                        newScale = (startWidth + offsetX) * (startScaleX / startWidth);
                    } else if (handleX === 0 && handleY === 0.5) {
                        // Middle left
                        newScale = (startWidth - offsetX) * (startScaleX / startWidth);
                    } else if (handleX === 0.5 && handleY === 0) {
                        // Middle top
                        newScale = (startHeight - offsetY) * (startScaleY / startHeight);
                    } else if (handleX === 0.5 && handleY === 1) {
                        // Middle bottom
                        newScale = (startHeight + offsetY) * (startScaleY / startHeight);
                    } else if (handleX === 0 && handleY === 0) {
                        // Top left
                        let newScaleX = (startWidth - offsetX) * (startScaleX / startWidth);
                        let newScaleY = (startHeight - offsetY) * (startScaleY / startHeight);

                        newScale = Math.max(newScaleX, newScaleY);
                    } else if (handleX === 1 && handleY === 0) {
                        // Top right
                        let newScaleX = (startWidth + offsetX) * (startScaleX / startWidth);
                        let newScaleY = (startHeight - offsetY) * (startScaleY / startHeight);

                        newScale = Math.max(newScaleX, newScaleY);
                    } else if (handleX === 0 && handleY === 1) {
                        // Bottom left
                        let newScaleX = (startWidth - offsetX) * (startScaleX / startWidth);
                        let newScaleY = (startHeight + offsetY) * (startScaleY / startHeight);

                        newScale = Math.max(newScaleX, newScaleY);
                    } else if (handleX === 1 && handleY === 1) {
                        // Bottom right
                        let newScaleX = (startWidth + offsetX) * (startScaleX / startWidth);
                        let newScaleY = (startHeight + offsetY) * (startScaleY / startHeight);

                        newScale = Math.max(newScaleX, newScaleY);
                    }

                    if (newScale === 0) {
                        return;
                    }

                    this.resizing.container.target.scale.x = Math.max(0.01, newScale);
                    this.resizing.container.target.scale.y = Math.max(0.01, newScale);

                    if (this.resizing.container.start.scaleX < 0) {
                        this.resizing.container.target.scale.x = -this.resizing.container.target.scale.x;
                    }
                    if (this.resizing.container.start.scaleY < 0) {
                        this.resizing.container.target.scale.y = -this.resizing.container.target.scale.y;
                    }

                    this.resizing.container.target.children.filter(child => child.type === Type.Resizer).forEach(child => {
                        this.resizeHandle(child);
                    });

                    this.markChange('resize');
                }
            },

            stopDragging() {
                this.resizing = null;

                if (this.dragging) {
                    this.dragging.container.target.alpha = 1;
                    this.dragging = null;
                }
            },

            markChange(type) {
                this.hasAnyEditorChanges = true;
                console.log('Marked change from ' + type);
            },

            async reset() {
                this.hasAnyEditorChanges = false;

                const toRemove = child => child.type === Type.Image;

                this.editorExportContainer.children.filter(toRemove).forEach(child => {
                    this.editorExportContainer.removeChild(child);
                });

                this.updateRenderState();
            },

            async createEditorContainer(width, height) {
                const editor = new Container();

                const backgroundAsset = await Texture.fromURL(require('../assets/file-editor/grid.png'), {
                    wrapMode: WRAP_MODES.REPEAT,
                });

                const editorBackground = TilingSprite.from(backgroundAsset, {
                    width,
                    height,
                });
                editorBackground.type = Type.EditorBackground;
                editor.addChild(editorBackground);

                const editorExportContainer = new Container();
                editorExportContainer.type = Type.EditorExport;
                editor.addChild(editorExportContainer);

                const borderSize = 2000;
                const borders = [
                    // Left
                    {
                        x: -borderSize,
                        y: 0,
                        width: borderSize,
                        height,
                    },
                    // Right
                    {
                        x: width,
                        y: 0,
                        width: borderSize,
                        height,
                    },
                    // Top
                    {
                        x: -borderSize,
                        y: -borderSize,
                        width: borderSize + width + borderSize,
                        height: borderSize,
                    },
                    // Bottom
                    {
                        x: -borderSize,
                        y: height,
                        width: borderSize + width + borderSize,
                        height: borderSize,
                    },
                ];

                const borderGraphics = new Graphics();
                borderGraphics.eventMode = 'none';

                borderGraphics.beginFill('#555555', 0.7);

                for (const border of borders) {
                    borderGraphics.drawRect(
                        border.x,
                        border.y,
                        border.width,
                        border.height,
                    );
                }

                editor.addChild(borderGraphics);

                return editor;
            },

            fitEditorToScreen(editor, screenWidth, screenHeight) {
                console.log('Fit editor to screen');
                const borderSize = 30;
                const editorWidth = editor.children[0].width;
                const editorHeight = editor.children[0].height;

                const renderWidth = Math.max(screenWidth - (borderSize * 2), screenWidth - (borderSize * 2) - editorWidth);
                const renderHeight = Math.max(screenHeight - (borderSize * 2), screenHeight - (borderSize * 2) - editorHeight);

                const scaleX = renderWidth / editorWidth;
                const scaleY = renderHeight / editorHeight;

                const scale = Math.min(scaleX, scaleY);

                editor.scale.x = scale;
                editor.scale.y = scale;

                editor.position.x = (screenWidth / 2) - ((editorWidth * scale) / 2);
                editor.position.y = (screenHeight / 2) - ((editorHeight * scale) / 2);
            },

            fitImageToEditor(imageContainer, contain = false) {
                const editorWidth = this.editorBackgroundContainer.width;
                const editorHeight = this.editorBackgroundContainer.height;

                const image = imageContainer.children.find(child => child.export);
                const scaleX = editorWidth / (image.texture?.width || image.width);
                const scaleY = editorHeight / (image.texture?.height || image.height);

                if (contain) {
                    console.log('Fit image to editor: contain');
                    const scale = Math.min(scaleX, scaleY);

                    imageContainer.scale.x = scale;
                    imageContainer.scale.y = scale;
                } else {
                    console.log('Fit image to editor: cover');

                    const scale = Math.max(scaleX, scaleY);

                    imageContainer.scale.x = scale;
                    imageContainer.scale.y = scale;
                }

                imageContainer.position.x = editorWidth / 2;
                imageContainer.position.y = editorHeight / 2;

                imageContainer.children.filter(child => child.type === Type.Resizer).forEach(child => {
                    this.resizeHandle(child);
                });
            },

            async addFromFileEditorData(editorData) {
                await Promise.all(editorData.map(async data => {
                    switch (data.type) {
                        case Type.Image:
                            return this.addImage(
                                data.url,
                                data.imageType,
                                data.transform,
                                data.border,
                                data.background,
                            );
                    }
                }));
            },

            async addImage(url, imageType, transform = null, border = null, background = null) {
                const imageContainer = new Container();
                imageContainer.type = Type.Image;
                imageContainer.imageType = imageType;
                imageContainer.exportData = {
                    url: (
                        url.startsWith('https://') ||
                        url.startsWith('http://') ||
                        url.startsWith('://')
                    ) ? (new URL(url)).pathname : url,
                    type: Type.Image,
                    imageType,
                };

                const asset = await Assets.load(url);
                const mainItem = Sprite.from(asset);
                mainItem.export = true;

                // The main image has the outline filter, which is default just hidden
                if (imageType === ImageTypes.Main) {
                    const outlineFilter = new OutlineFilter(border?.size ?? 0, border?.color ? hexToDecimal(border.color) : 0xFFFFFF, 1);
                    outlineFilter.type = FilterType.Outline;
                    mainItem.filters = [outlineFilter];
                }

                mainItem.anchor.set(0.5, 0.5);
                imageContainer.addChild(mainItem);

                if (transform && transform.container && transform.item) {
                    mainItem.width = transform.item.width;
                    mainItem.height = transform.item.height;
                    mainItem.rotation = transform.item.rotation ?? 0;

                    imageContainer.position.x = transform.container.position?.x ?? 1;
                    imageContainer.position.y = transform.container.position?.y ?? 1;
                    imageContainer.rotation = transform.container.rotation ?? 0;
                    imageContainer.scale.x = transform.container.scale?.x ?? 1;
                    imageContainer.scale.y = transform.container.scale?.y ?? 1;
                } else {
                    mainItem.width = asset.width;
                    mainItem.height = asset.height;
                }

                // Only the main image is interactive
                if (imageType === ImageTypes.Main) {
                    this.makeContainerInteractive(imageContainer);
                }

                // Add the background overlay when we have a theme image
                if (imageType === ImageTypes.Theme) {
                    const backgroundColorOverlay = new Sprite(Texture.WHITE);
                    backgroundColorOverlay.export = true;

                    backgroundColorOverlay.tint = background?.color ?? '#000000';
                    backgroundColorOverlay.alpha = background?.opacity ?? 0;

                    backgroundColorOverlay.type = Type.BackgroundOverlay;

                    backgroundColorOverlay.width = mainItem.width;
                    backgroundColorOverlay.height = mainItem.height;

                    backgroundColorOverlay.x = -(mainItem.width / 2);
                    backgroundColorOverlay.y = -(mainItem.height / 2);

                    imageContainer.addChild(backgroundColorOverlay);
                }

                this.editorExportContainer.addChild(imageContainer);

                if (!transform) {
                    this.fitImageToEditor(imageContainer, true);
                }

                // We want the theme images on the bottom
                this.editorExportContainer.children.sort((a, b) => {
                    if (a.imageType === ImageTypes.Theme && b.imageType !== ImageTypes.Theme) {
                        return -1;
                    }
                    if (a.imageType !== ImageTypes.Theme && b.imageType === ImageTypes.Theme) {
                        return 1;
                    }

                    return 0;
                });

                imageContainer.children.filter(child => child.type === Type.Resizer).forEach(child => {
                    this.resizeHandle(child);
                });

                return imageContainer;
            },

            async selectedTheme(file) {
                this.loading = true;

                try {
                    this.removeTheme();

                    if (file.type === FILE_EDITOR_THEME_TYPE.IMAGE) {
                        const themeImage = await this.addImage(file.url, ImageTypes.Theme);
                        this.fitImageToEditor(themeImage, false);
                    } else {
                        console.error('Not supported yet.');
                    }

                    this.markChange('set-theme');
                    this.updateRenderState();
                } catch (exception) {
                    notifyError(exception);
                } finally {
                    this.loading = false;
                }

            },

            removeTheme() {
                const existingThemes = this.editorExportContainer.children.filter(child => child.imageType === ImageTypes.Theme);

                // Remove the existing themes
                existingThemes.forEach(existingTheme => {
                    this.editorExportContainer.removeChild(existingTheme);
                });

                this.markChange('remove-theme');
                this.updateRenderState();
            },

            makeContainerInteractive(container) {
                container.eventMode = 'static';
                container.cursor = 'pointer';

                const handleSize = HANDLE_SIZE;

                const resizerBlocks = [
                    // Bottom right
                    {
                        x: 0,
                        y: 0,
                        cursor: 'se-resize',
                    },
                    // Bottom middle
                    {
                        x: 0.5,
                        y: 0,
                        cursor: 's-resize',
                    },
                    // Bottom left
                    {
                        x: 1,
                        y: 0,
                        cursor: 'sw-resize',
                    },

                    // Center right
                    {
                        x: 0,
                        y: 0.5,
                        cursor: 'e-resize',
                    },
                    // Center left
                    {
                        x: 1,
                        y: 0.5,
                        cursor: 'w-resize',
                    },

                    // Top right
                    {
                        x: 0,
                        y: 1,
                        cursor: 'ne-resize',
                    },
                    // Top middle
                    {
                        x: 0.5,
                        y: 1,
                        cursor: 'n-resize',
                    },
                    // Top left
                    {
                        x: 1,
                        y: 1,
                        cursor: 'nw-resize',
                    },
                ];

                for (const resizerBlock of resizerBlocks) {
                    const handle = new Sprite(Texture.WHITE);
                    handle.type = Type.Resizer;

                    handle.baseX = resizerBlock.x;
                    handle.baseY = resizerBlock.y;

                    handle.width = handleSize;
                    handle.height = handleSize;

                    handle.eventMode = 'static';
                    handle.cursor = resizerBlock.cursor;

                    handle.on('pointerdown', (event) => {
                        event.stopPropagation();

                        this.resizing = {
                            handle: {
                                target: event.currentTarget,
                                start: {
                                    x: event.currentTarget.position.x,
                                    y: event.currentTarget.position.y,

                                    baseX: event.currentTarget.baseX,
                                    baseY: event.currentTarget.baseY,
                                },
                            },

                            container: {
                                target: container,
                                start: {
                                    x: container.position.x,
                                    y: container.position.y,

                                    rotation: container.rotation,

                                    width: container.width,
                                    height: container.height,

                                    scaleX: container.scale.x,
                                    scaleY: container.scale.y,

                                    mouseX: event.clientX,
                                    mouseY: event.clientY,

                                    globalX: event.global.x,
                                    globalY: event.global.y,
                                },
                            },
                        };
                    });

                    container.addChild(handle);

                    this.resizeHandle(handle);
                }

                container.on('pointerdown', (event) => {
                    container.alpha = 0.5;

                    this.dragging = {
                        container: {
                            target: container,

                            start: {
                                x: container.position.x,
                                y: container.position.y,

                                globalX: event.global.x,
                                globalY: event.global.y,
                            },
                        },
                    };
                });
            },

            resizeHandle(handle) {
                const originalWidth = handle.parent.children[0].width;
                const originalHeight = handle.parent.children[0].height;
                const rotation = handle.parent.children[0].rotation / DEG_TO_RAD;

                let imageWidth = originalWidth;
                let imageHeight = originalHeight;

                // This should be matrix calculations...
                if (rotation === 90 || rotation === 270) {
                    imageWidth = originalHeight;
                    imageHeight = originalWidth;
                }

                handle.width = HANDLE_SIZE / Math.abs(handle.parent.scale.x);
                handle.height = HANDLE_SIZE / Math.abs(handle.parent.scale.y);

                handle.x = (imageWidth / 2) + -((imageWidth / 2) + (handle.width / 2)) * (handle.baseX / 0.5);
                handle.y = (imageHeight / 2) + -((imageHeight / 2) + (handle.height / 2)) * (handle.baseY / 0.5);
            },

            rotate(direction) {
                this.editorExportContainer.children.filter(child => child.imageType === ImageTypes.Main).forEach(child => {
                    // Only rotate the first child since the rest of the children are the resize handlers, we do not want to rotate those
                    // We need to convert the pixi radians to degrees for easier calculations :)
                    let newRotation = (child.children[0].rotation / DEG_TO_RAD) + (direction === 'left' ? -90 : 90);

                    if (newRotation > 360) {
                        newRotation = 90;
                    } else if (newRotation < 0) {
                        newRotation = 270;
                    }

                    child.children[0].rotation = newRotation / RAD_TO_DEG;

                    child.children.filter(child => child.type === Type.Resizer).forEach(child => {
                        this.resizeHandle(child);
                    });
                });

                this.markChange('rotate');
            },

            scale(direction) {
                this.editorExportContainer.children.filter(child => child.imageType === ImageTypes.Main).forEach(child => {
                    if (direction === 'horizontal') {
                        child.scale.x = -child.scale.x;
                    } else if (direction === 'vertical') {
                        child.scale.y = -child.scale.y;
                    }
                });

                this.markChange('scale');
            },

            changeBorderSize(size, update = true) {
                if (!this.currentBorderFilter) {
                    return;
                }

                this.currentBorderFilter.thickness = size;

                if (update) {
                    this.markChange('border-size');
                }
            },

            changeBorderColor(color, update = true) {
                if (!this.currentBorderFilter) {
                    return;
                }

                this.currentBorderFilter.color = color;
                this.currentBorderColor = color;

                if (update) {
                    this.markChange('border-color');
                }
            },

            changeBackgroundColor(color, update = true) {
                if (!this.currentBackgroundOverlay) {
                    return;
                }

                this.currentBackgroundOverlay.tint = color;

                if (update) {
                    this.markChange('background-color');
                }
            },

            changeBackgroundColorOpacity(opacity, update = true) {
                if (!this.currentBackgroundOverlay) {
                    return;
                }

                this.currentBackgroundOverlay.alpha = opacity;

                if (update) {
                    this.markChange('background-opacity');
                }
            },

            async closeConfirm() {
                if (this.hasAnyEditorChanges) {
                    if (confirm(this.$t('files.do_you_want_to_save_your_changes'))) {
                        await this.saveAndClose();
                    }
                }

                this.$emit('close');
            },

            setAspectRatio() {
                if (this.lockAspectRatio) {
                    this.lockAspectRatio = false;
                } else {
                    this.lockAspectRatio = true;
                }

                this.editorExportContainer.children.filter(child => child.imageType === ImageTypes.Main).forEach(child => {
                    this.fitImageToEditor(child, this.lockAspectRatio);
                });

                this.markChange('aspectRatio');
            },

            async generateImage() {
                // Hide all children not for export
                this.editorExportContainer.children.forEach(child => {
                    child.children.filter(subChild => !subChild.export).forEach(subChild => {
                        subChild.visible = false;
                    });
                });

                // Generate the image
                const renderer = autoDetectRenderer();
                const texture = RenderTexture.create({
                    width: this.editorBackgroundContainer.width,
                    height: this.editorBackgroundContainer.height,
                });

                renderer.render(this.editorExportContainer, {
                    renderTexture: texture,
                });

                const image = await renderer.extract.base64(texture, 'image/png', 1);
                renderer.destroy();

                // Show all children not for export
                this.editorExportContainer.children.forEach(child => {
                    child.children.filter(subChild => !subChild.export).forEach(subChild => {
                        subChild.visible = true;
                    });
                });

                return image;
            },

            generateEditorData() {
                return this.editorExportContainer.children.filter(child => !!child.exportData).reduce((exportData, container) => {
                    const image = container.children.find(child => child.export);

                    if (!image) {
                        return exportData;
                    }

                    const borderFilter = (image.filters ?? []).find(filter => filter.type === FilterType.Outline);
                    const backgroundOverlay = (container?.children ?? []).find(child => child.type === Type.BackgroundOverlay);

                    exportData.push({
                        ...container.exportData,

                        transform: {
                            container: {
                                width: container.width,
                                height: container.height,
                                rotation: container.rotation,

                                position: {
                                    x: container.position.x,
                                    y: container.position.y,
                                },

                                scale: {
                                    x: container.scale.x,
                                    y: container.scale.y,
                                },
                            },

                            item: {
                                width: image.width,
                                height: image.height,
                                rotation: image.rotation,

                                position: {
                                    x: image.position.x,
                                    y: image.position.y,
                                },

                                scale: {
                                    x: image.scale.x,
                                    y: image.scale.y,
                                },
                            },
                        },

                        border: borderFilter ? {
                            size: borderFilter.thickness,
                            color: decimalToHexString(borderFilter.color),
                        } : null,

                        background: backgroundOverlay ? {
                            opacity: backgroundOverlay.alpha,
                            color: backgroundOverlay.tint,
                        } : null,
                    });

                    return exportData;
                }, []);
            },

            async save() {
                this.loading = true;

                try {
                    return await this.$http.put(`/api/ceremonies/code/${this.ceremonyCode}/files/${this.currentFile.id}`, {
                        ...this.requestData,
                        block_id: this.block.id,
                        editor_data: this.hasAnyEditorChanges && this.renderer ? this.generateEditorData() : undefined,
                        updated_image: this.hasAnyEditorChanges && this.renderer ? await this.generateImage() : undefined,
                    });
                } catch (exception) {
                    Sentry.captureException(exception);
                    console.error(exception);
                    this.$notify({
                        group: 'default',
                        title: this.$t('general.something_went_wrong'),
                        text: this.$t('general.something_went_wrong'),
                        type: 'error',
                        duration: 8000,
                        position: 'top center',
                    });

                    throw exception;
                } finally {
                    this.loading = false;
                }

            },

            async saveAndGoto(file) {
                try {
                    const response = await this.save();

                    this.$emit('file-updated', response.data);
                } catch (exception) {
                    // nothing to do since the handling is done in the save.
                }

                this.currentFile = file;
            },

            async saveAndClose() {
                try {
                    const response = await this.save();

                    this.$emit('save', response.data);
                    this.$emit('file-updated', response.data);
                } catch (exception) {
                    // nothing to do since the handling is done in the save.
                }
            },

            // For window events we want a short delay
            resizeWindow: debounce(function() {
                if (!this.renderer?.app?.screen) {
                    return;
                }

                this.fitEditorToScreen(this.renderer.editor, this.renderer.app.screen.width, this.renderer.app.screen.height);
            }, 1000),

            removeEditor() {
                if (this.initialized) {
                    this.renderer.app.destroy();

                    window.removeEventListener('resize', this.resizeWindow);
                    window.removeEventListener('pointermove', this.resizeMouseMove);
                    window.removeEventListener('pointerup', this.stopDragging);
                }

                this.renderer = null;
            },
        },

        beforeDestroy() {
            this.removeEditor();
        },
    };

</script>

<style lang="scss" scoped>

    .cropper-wrapper {
        position: relative;

        &.disabled {
            cursor: not-allowed;

            .editor-canvas {
                pointer-events: none;
            }
        }

        .editor-canvas {
            width: 100%;
            min-height: 500px;
            height: 50vh;
        }

        .cropper-loading {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background-color: rgba(0, 0, 0, 0.5);
            z-index: 100;
            display: flex;
            align-items: center;
            justify-content: center;
            color: #fff;
        }
    }

    .editor-actions {
        display: flex;
        align-items: center;

        button {
            padding: 0.5rem 0.75rem;
        }
    }

    .editor-footer {
        display: flex;
        width: 100%;

        @media(max-width: 767px) {
            display: block;
            text-align: right;

            .editor-actions {
                margin-bottom: 15px;
                text-align: center;
            }
        }
    }

    .btn-color-range {
        padding-left: 5px;
        padding-top: 4px;
        padding-bottom: 4px;

        .color-picker {
            width: 30px;
            padding: 0;
            appearance: none;
            background: none;
            cursor: pointer;
            border-radius: 3px;
            margin: 0;
            margin-right: 10px;
            border: 1px solid #273444;

            input {
                opacity: 0;
                pointer-events: none;
            }
        }
    }
</style>
